import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import router from '@/router';
// Matab/clinic is temporarily disabled in user area because its backend view is unavailable.
// import { usePatientsStore } from '@/stores/clinic/patients';
import { useToast } from 'vue-toastification';
import { toPersianDigits, formatJalaliDate, formatCurrency } from '@/utils/formatters.js';
import apiClient from '@/axios';
import { getLicenseStatus } from '@/utils/licenseStatus';
import { usetariffStore } from '@/stores/common/tariff'
import { usePepTransactionStore } from '@/stores/common/pepTransaction.js';
import { useAuthenticateStore } from '@/stores/common/authenticate';

export const useUserAreaStore = defineStore('userArea', () => {
const toast = useToast();
// const patientsStore = usePatientsStore();
const tariffStore = usetariffStore();
const pepTransaction = usePepTransactionStore();
const authenticate = useAuthenticateStore();

const companyTitle = ref('');
const panelNumber = ref('');
const activeServices = ref(3);
const messages = ref(0);
const usedStorage = ref(0); // MB
const totalStorage = ref(1000); // MB
const storagePercent = computed(() => (totalStorage.value ? (usedStorage.value / totalStorage.value) * 100 : 0));
const recentActivities = ref([]);
const lastSettingsUpdate = ref('');
const lastLogin = ref('');
const attachedFilesCount = ref(0);
const attachmentsSeries = ref([2, 5, 3, 6, 4, 8, 7]);
const maxAttachments = computed(() => Math.max(...attachmentsSeries.value, 1));

// license / subscription info to display in profile card
const purchaseDate = ref('-');
const expiryDate = ref('-');
const licenseExpiryRaw = ref(null);
const licenseInfoLoading = ref(false);
const licenseStatus = computed(() => getLicenseStatus(licenseExpiryRaw.value).text);
const licenseStatusTone = computed(() => getLicenseStatus(licenseExpiryRaw.value).tone);
// Info link for PageHeader help
// const infoLearnUrl = 'https://www.sinapardazesh.ir/matab-learning-web/'
const infoLearnUrl = ref('https://www.sinapardazesh.ir/')

// const patientsCount = ref(0);

const showChangePassword = ref(false);
const oldPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showMobileModal = ref(false);
const mobileSendLoading = ref(false);
const mobileCodeSent = ref(false);
const mobileVerificationCode = ref('');
const newMobileNumber = ref('');
const generatedMobileCode = ref('');
const resendSeconds = ref(0);
let resendInterval = null;
const showPasswordModal = ref(false);
const passwordSendLoading = ref(false);
const submitPasswordChangeLoading = ref(false);
const passwordCodeSent = ref(false);
const passwordVerificationCode = ref('');
const passwordNew = ref('');
const passwordConfirmNew = ref('');
const generatedPasswordCode = ref('');
const resendSecondsPass = ref(0);
let resendIntervalPass = null;
const showTwoFactorMethodModal = ref(false);
const selectedTwoFactorMethod = ref('');
const twoFactorLoading = ref(false);
const twoFactorError = ref('');
const googleSetupData = ref(null);
const googleVerificationCode = ref('');
const twoFactorStatus = ref({
  enabled: false,
  method: '',
  methodLabel: ''
});
const twoFactorMethods = ref([
  { id: 'sms', label: 'پیامک', link: '' },
  {
    id: 'google_authenticator',
    label: 'Google Authenticator',
    link: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=fa'
  },
  { id: 'gmail', label: 'جیمیل', link: '' }
]);
const showRenewalModal = ref(false);
const renewalPaymentLoading = ref(false);
const selectedRenewalGateway = ref('zarinpal');
const renewalAmount = ref(26400000);
const renewalGateways = ref([
  { id: 'zarinpal', label: 'درگاه زرین‌پال' },
  { id: 'saman', label: 'درگاه سامان' }
]);

function persistTwoFactorStatus() {
  localStorage.setItem('twoFactorStatus', JSON.stringify(twoFactorStatus.value));
}

function loadTwoFactorStatus() {
  try {
    const raw = localStorage.getItem('twoFactorStatus');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      twoFactorStatus.value = {
        enabled: Boolean(parsed.enabled),
        method: parsed.method || '',
        methodLabel: parsed.methodLabel || ''
      };
    }
  } catch (err) {
    console.warn('Unable to load twoFactorStatus from localStorage:', err);
  }
}

function resolveMethodLabel(methodId) {
  const found = twoFactorMethods.value.find(method => method.id === methodId);
  return found?.label || methodId || '';
}

function createMockGoogleSetup() {
  const secret = 'JBSWY3DPEHPK3PXP';
  const issuer = encodeURIComponent('Sinapack');
  const accountName = encodeURIComponent(accountInfo.value.mobileNumber || 'user@example.com');
  const otpAuthUri = `otpauth://totp/${issuer}:${accountName}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
  return {
    secret,
    otpAuthUri,
    qrCodeImageUrl: ''
  };
}

const monthlyBills = ref({ count: 0, total: 0 });
const monthStart = ref('');

const selectedTariffIds = ref([]);
const showTariffInvoiceModal = ref(false);
const tariffPurchaseLoading = ref(false);
const tariffLoading = computed(() => tariffStore.loading);
const tariffItems = computed(() => (Array.isArray(tariffStore.tariffEntity) ? tariffStore.tariffEntity : []));

const selectedTariffItems = computed(() => {
  const ids = new Set(selectedTariffIds.value);
  return tariffItems.value.filter(t => ids.has(t.id));
});

const selectedTariffTotal = computed(() => {
  return selectedTariffItems.value.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
});

// account information for editing
const accountInfo = ref({
  id: 0,
  firstName: '',
  lastName: '',
  title: '',
  latinTitle: '',
  description: '',
  companyImageString: '',
  companyImageSize: null,
  companyImageType: null,
  address: '',
  nationalCode: '',
  postalCode: '',
  phoneNumber: '',
  commercialCode: '',
  panelNumber: '',
  mobileNumber: ''
});

const imageInput = ref(null);

const companyFields = computed(() => [
  { label: 'عنوان لاتین', value: accountInfo.value.latinTitle, ltr: true },
  { label: 'شناسه ملی', value: accountInfo.value.nationalCode, ltr: true },
  { label: 'کد پستی', value: accountInfo.value.postalCode, ltr: true },
  { label: 'تلفن', value: accountInfo.value.phoneNumber, ltr: true },
  { label: 'شماره همراه', value: accountInfo.value.mobileNumber, ltr: true },
  { label: 'شماره پنل', value: accountInfo.value.panelNumber || panelNumber.value, ltr: true },
  { label: 'نشانی', value: accountInfo.value.address }
]);

function displayValue(value) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

function triggerImageUpload() {
  imageInput.value?.click();
}

function handleImageUpload(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type (only JPG/JPEG)
  if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
    toast.error('آپلود عکس فقط با فرمت jpg و حجم کمتر از 200 کیلوبایت میسر می باشد.');
    event.target.value = ''; // Reset input
    return;
  }

  // Validate file size (max 200KB)
  if (file.size > 200 * 1024) {
    toast.error('آپلود عکس فقط با فرمت jpg و حجم کمتر از 200 کیلوبایت میسر می باشد.');
    event.target.value = ''; // Reset input
    return;
  }

  // Convert to base64
  const reader = new FileReader();
  reader.onload = (e) => {
    accountInfo.value.companyImageString = e.target?.result;
    accountInfo.value.companyImageSize = file.size;
    accountInfo.value.companyImageType = file.type;

    const companyInfo = JSON.parse(localStorage.getItem('companyInfo') || '{}');
    localStorage.setItem('companyInfo', JSON.stringify({ ...companyInfo, ...accountInfo.value }));
    window.dispatchEvent(new Event('company-image-updated'));
  };
  reader.onerror = () => {
    toast.error('خطا در خواندن فایل');
    event.target.value = ''; // Reset input
  };
  reader.readAsDataURL(file);
}

async function confirmTariffPurchase() {
  if (selectedTariffIds.value.length === 0) {
    toast.error('لطفاً حداقل یک تعرفه را انتخاب کنید');
    return;
  }

  tariffPurchaseLoading.value = true;
  try {
    const result = await pepTransaction.requestPepTransaction(selectedTariffIds.value);

    if (result && Number(result.status) === 0 && result.redirectUrl) {
      showTariffInvoiceModal.value = false;
      selectedTariffIds.value = [];
      window.location.assign(result.redirectUrl);
      return;
    }

    toast.error('خطا در دریافت لینک پرداخت از بانک');
  } catch (e) {
    // toast handled in store
  } finally {
    tariffPurchaseLoading.value = false;
  }
}

function openTariffInvoiceModal() {
  if (selectedTariffIds.value.length === 0) {
    toast.error('لطفاً حداقل یک تعرفه را انتخاب کنید');
    return;
  }
  showTariffInvoiceModal.value = true;
}

function cancelTariffPurchase() {
  showTariffInvoiceModal.value = false;
  selectedTariffIds.value = [];
}

function formatLicenseDate(value) {
  if (!value) return '-';

  if (value instanceof Date) {
    return formatJalaliDate(value.toISOString().slice(0, 10));
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : formatJalaliDate(date.toISOString().slice(0, 10));
  }

  const text = String(value).trim();
  const microsoftDateMatch = text.match(/\/Date\((\d+)\)\//);
  if (microsoftDateMatch) {
    const date = new Date(Number(microsoftDateMatch[1]));
    return Number.isNaN(date.getTime()) ? '-' : formatJalaliDate(date.toISOString().slice(0, 10));
  }

  const dateOnly = text.split('T')[0].replace(/-/g, '/');
  if (/^1[34]\d{2}\/\d{1,2}\/\d{1,2}$/.test(dateOnly)) {
    return toPersianDigits(dateOnly);
  }

  if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(dateOnly)) {
    return formatJalaliDate(dateOnly.replace(/\//g, '-'));
  }

  return toPersianDigits(text);
}

async function fetchLicenseInfo() {
  licenseInfoLoading.value = true;
  try {
    const { data } = await apiClient.get(`/LoginInformation/GetLoginInformation?isMaster=${localStorage.getItem('ismaster')}`);
    const licenseInfo = data?.licenseInfo || {};
    licenseExpiryRaw.value = licenseInfo.expireDate || null;
    purchaseDate.value = formatLicenseDate(licenseInfo.createdDate);
    expiryDate.value = formatLicenseDate(licenseExpiryRaw.value);
  } catch (err) {
    console.log(err);
  } finally {
    licenseInfoLoading.value = false;
  }
}

async function initUserAreaPage() {
  loadTwoFactorStatus();
  const c = JSON.parse(localStorage.getItem('companyInfo')) || {};
  companyTitle.value = c.title || '';
  panelNumber.value = c.panelNumber || '';

  const storageInfo = JSON.parse(localStorage.getItem('storageInfo')) || {};
  if (storageInfo) {
    usedStorage.value = storageInfo.totalSizeMB
    attachedFilesCount.value = storageInfo.fileCount
  }


  // sample recent activities (could be fetched)
  recentActivities.value = [
    { title: 'ورود به سامانه', time: 'یک ساعت پیش' },
    { title: 'به‌روزرسانی تنظیمات', time: '۳ روز پیش' },
  ];

  // populate last login / settings update from recent activities if available
  const loginAct = recentActivities.value.find(a => a.title.includes('ورود'));
  const settingsAct = recentActivities.value.find(a => a.title.includes('به‌روزرسانی') || a.title.includes('تنظیمات'));
  lastLogin.value = loginAct ? loginAct.time : '';
  lastSettingsUpdate.value = settingsAct ? settingsAct.time : '';

  await fetchLicenseInfo();

  // Matab/clinic patients count is temporarily disabled because V_MatabPersons is unavailable.
  // try {
  //   await patientsStore.fetchPatients(1);
  //   patientsCount.value = patientsStore.pagination?.total || patientsStore.patientsList.length || 0;
  // } catch (err) {
  //   // ignore
  // }

  // load tariffs for subsystem purchase/activation
  try {
    await tariffStore.fetchtariff();
  } catch (e) {
    // best-effort
  }

  // sample monthly bills data (could be fetched from API)
  // using requested demo values
  monthStart.value = '2025-10-31';
  monthlyBills.value = { count: 12, total: 1250000 };

  // Load account info from companyInfo or initialize with default values
  accountInfo.value = {
    id: c.id || 0,
    firstName: c.firstName || '',
    lastName: c.lastName || '',
    title: c.title || 'دمو اتوماسیون سیناپک',
    latinTitle: c.latinTitle || '',
    description: c.description || 'SinapardazeshSoft',
    companyImageString: c.companyImageString || '',
    companyImageSize: c.companyImageSize || null,
    companyImageType: c.companyImageType || null,
    address: c.address || 'تهران، سهروردی شمالی',
    nationalCode: c.nationalCode || '11111111',
    postalCode: c.postalCode || '11111111',
    phoneNumber: c.phoneNumber || '02188745474',
    commercialCode: c.commercialCode || '11111111',
    panelNumber: c.panelNumber || '',
    mobileNumber: c.mobileNumber || '09213560531'
  };

}

function goToSettings() { router.push('/overview/common/app-settings?tab=general'); }
function goToSubsystems() {
  // Scroll to subsystems section
  const subsystemsSection = document.querySelector('.subsystem-panel');
  if (subsystemsSection) {
    subsystemsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
function openBilling() { router.push('/overview/common/paid-invoices'); }
function openSupport() {
  window.open('https://sinapardazesh.ir/panel/ticket', '_blank', 'noopener,noreferrer');
}
function manageSessions() { /* navigate or show modal */ }

function buyStorage() {
  // navigate to billing/purchase page
  router.push('/overview/common/app-settings?tab=billing');
}

function renewLicense() {
  showRenewalModal.value = true;
  selectedRenewalGateway.value = 'zarinpal';
}

function closeRenewalModal() {
  if (renewalPaymentLoading.value) return;
  showRenewalModal.value = false;
}

function getCompanyIdForPayment() {
  const storedId = localStorage.getItem('id');
  if (Number(storedId)) return Number(storedId);

  const companyInfo = JSON.parse(localStorage.getItem('companyInfo')) || {};
  return Number(companyInfo.id || accountInfo.value.id || 0);
}

function extractPaymentUrl(payload) {
  if (typeof payload === 'string') return payload;
  return payload?.redirectUrl
    || payload?.url
    || payload?.paymentUrl
    || payload?.data
    || payload?.result
    || '';
}

async function payRenewal() {
  if (!selectedRenewalGateway.value) {
    toast.error('لطفاً یک درگاه پرداخت را انتخاب کنید');
    return;
  }

  const companyId = getCompanyIdForPayment();
  if (!companyId) {
    toast.error('شناسه شرکت برای پرداخت یافت نشد');
    return;
  }

  renewalPaymentLoading.value = true;
  try {
    const paymentPayload = {
      companyId,
      description: `${panelNumber.value || accountInfo.value.panelNumber || ''} تمدید اشتراک سامانه`,
      callbackurl: 'https://sm.sinapardazesh.ir/admin/payResult'
    };

    if (selectedRenewalGateway.value === 'saman') {
      const response = await apiClient.post('/SepTransaction/Payment', paymentPayload);
      if (response.data.status === 'success') {
        window.location.href = response.data.redirectUrl;
      } else {
        alert('ارتباط با درگاه سامان در حال حاضر میسر نیست لطفا درگاه دیگری انتخاب نمایید.');
      }
      return;
    }

    const { data } = await apiClient.post('/Zarinpal/Payment', paymentPayload);

    const paymentUrl = extractPaymentUrl(data);
    if (paymentUrl) {
      window.location.replace(paymentUrl);
      return;
    }

    toast.error('خطا در دریافت لینک پرداخت از زرین‌پال');
  } catch (err) {
    console.log(err);
    if (err.response?.status === 401) {
      localStorage.clear();
      router.push('/');
    }
  } finally {
    renewalPaymentLoading.value = false;
  }
}

function goToFiles() {
  // Matab/clinic route is temporarily disabled.
  // router.push('/overview/clinic/patients');
  toast.info('بخش فایل‌های مطب فعلاً غیرفعال است.');
}

function openMonthlyBills() {
  // Matab/clinic reports route is temporarily disabled.
  // router.push({ path: '/overview/clinic/reports', query: { report: 'bills', from: monthStart.value } });
  toast.info('گزارش‌های مطب فعلاً غیرفعال است.');
}

function submitChangePassword() {
  if (!oldPassword.value || !newPassword.value) {
    toast.error('لطفاً فیلدها را تکمیل کنید');
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    toast.error('کلمه‌های عبور مطابقت ندارند');
    return;
  }

  // Here you would call API to change password. We'll simulate success.
  setTimeout(() => {
    toast.success('کلمه عبور با موفقیت تغییر کرد');
    showChangePassword.value = false;
    oldPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
  }, 700);
}

function openMobileModal() {
  showMobileModal.value = true;
  mobileVerificationCode.value = '';
  newMobileNumber.value = '';
  mobileCodeSent.value = false;
  generatedMobileCode.value = '';
}

function openPasswordModal() {
  showPasswordModal.value = true;
  passwordVerificationCode.value = '';
  passwordNew.value = '';

  passwordCodeSent.value = false;
  generatedPasswordCode.value = '';
}

function closePasswordModal() {
  showPasswordModal.value = false;
  cleanupPasswordModal();
}

function openTwoFactorMethodModal() {
  openTwoFactorMethodModalEx();
}

function closeTwoFactorMethodModal() {
  closeTwoFactorMethodModalEx();
}

async function submitTwoFactorMethod() {
  if (googleSetupData.value !== undefined) {
    return submitTwoFactorMethodEx();
  }
  if (!selectedTwoFactorMethod.value) {
    toast.error('لطفاً یکی از روش‌های احراز هویت را انتخاب کنید');
    return;
  }
  const selectedMethod = twoFactorMethods.value.find(method => method.id === selectedTwoFactorMethod.value);
  toast.success(`روش ${selectedMethod?.label || 'انتخاب‌شده'} ثبت شد`);
  closeTwoFactorMethodModal();
}

function openTwoFactorMethodModalEx() {
  showTwoFactorMethodModal.value = true;
  twoFactorError.value = '';
  googleVerificationCode.value = '';
  if (twoFactorStatus.value.method) {
    selectedTwoFactorMethod.value = twoFactorStatus.value.method;
  }
}

function closeTwoFactorMethodModalEx() {
  showTwoFactorMethodModal.value = false;
  twoFactorLoading.value = false;
  twoFactorError.value = '';
  googleVerificationCode.value = '';
  googleSetupData.value = null;
}

async function startGoogleAuthenticatorSetup() {
  twoFactorError.value = '';
  twoFactorLoading.value = true;
  try {
    const response = await apiClient.post('/Authenticate/TwoFactor/Google/Setup', {});
    const data = response?.data || {};
    googleSetupData.value = {
      secret: data.secret || '',
      otpAuthUri: data.otpAuthUri || '',
      qrCodeImageUrl: data.qrCodeImageUrl || ''
    };

    if (!googleSetupData.value.secret) {
      throw new Error('اطلاعات Google Authenticator از سرور دریافت نشد.');
    }
  } catch (err) {
    googleSetupData.value = createMockGoogleSetup();
    toast.info('بک‌اند آماده نیست؛ فعلا از حالت نمایشی فرانت استفاده شد.');
  } finally {
    twoFactorLoading.value = false;
  }
}

async function disableTwoFactor() {
  twoFactorError.value = '';
  twoFactorLoading.value = true;
  try {
    try {
      await apiClient.post('/Authenticate/TwoFactor/Disable', {});
    } catch (err) {
      // بک‌اند هنوز آماده نیست؛ در فرانت وضعیت را تغییر می‌دهیم.
    }

    twoFactorStatus.value = {
      enabled: false,
      method: '',
      methodLabel: ''
    };
    persistTwoFactorStatus();
    selectedTwoFactorMethod.value = '';
    googleVerificationCode.value = '';
    googleSetupData.value = null;
    toast.success('احراز هویت دو مرحله‌ای غیرفعال شد');
  } finally {
    twoFactorLoading.value = false;
  }
}

async function submitTwoFactorMethodEx() {
  twoFactorError.value = '';
  if (!selectedTwoFactorMethod.value) {
    toast.error('لطفاً یکی از روش‌های احراز هویت را انتخاب کنید');
    return;
  }

  if (selectedTwoFactorMethod.value !== 'google_authenticator') {
    const selectedMethod = twoFactorMethods.value.find(method => method.id === selectedTwoFactorMethod.value);
    twoFactorStatus.value = {
      enabled: true,
      method: selectedTwoFactorMethod.value,
      methodLabel: selectedMethod?.label || ''
    };
    persistTwoFactorStatus();
    toast.success(`روش ${selectedMethod?.label || 'انتخاب‌شده'} ثبت شد`);
    closeTwoFactorMethodModalEx();
    return;
  }

  if (!googleSetupData.value?.secret) {
    twoFactorError.value = 'ابتدا QR/Secret را دریافت کنید.';
    return;
  }

  if (!/^\d{6}$/.test(googleVerificationCode.value)) {
    twoFactorError.value = 'کد تایید باید 6 رقمی باشد.';
    return;
  }

  twoFactorLoading.value = true;
  try {
    try {
      await apiClient.post('/Authenticate/TwoFactor/Google/VerifySetup', {
        code: googleVerificationCode.value,
        secret: googleSetupData.value.secret
      });
    } catch (err) {
      // بک‌اند هنوز آماده نیست؛ فرانت را برای فاز توسعه فعال می‌کنیم.
    }

    twoFactorStatus.value = {
      enabled: true,
      method: 'google_authenticator',
      methodLabel: resolveMethodLabel('google_authenticator')
    };
    persistTwoFactorStatus();
    toast.success('Google Authenticator با موفقیت فعال شد');
    closeTwoFactorMethodModalEx();
  } finally {
    twoFactorLoading.value = false;
  }
}

async function sendPasswordChangeCode() {
  passwordSendLoading.value = true;
  try {
    const payload = {
      mobileNumber: accountInfo.value.mobileNumber
    }
    await authenticate.sendOtpForChangePassword(payload);

  } catch (e) {
    // toast handled in store
  } finally {
    passwordSendLoading.value = false;
    passwordCodeSent.value = true;
  }
}

async function submitPasswordChangeModal() {
  if (!passwordCodeSent.value) { toast.error('لطفاً ابتدا کد را ارسال کنید'); return; }
  if (!passwordVerificationCode.value) { toast.error('کد تایید را وارد کنید'); return; }
  if (!passwordNew.value || passwordNew.value.length < 6) { toast.error('کلمه عبور جدید باید حداقل ۶ کاراکتر باشد'); return; }
  if (!passwordConfirmNew.value) { toast.error('لطفاً تکرار کلمه عبور را وارد کنید'); return; }
  if (passwordNew.value !== passwordConfirmNew.value) { toast.error('کلمه‌های عبور مطابقت ندارند'); return; }
  submitPasswordChangeLoading.value = true;
  try {
    const payload = {
      newPassword: passwordNew.value,
      otpCode: passwordVerificationCode.value,

    }
    await authenticate.changePassword(payload);

  } catch (e) {
    // toast handled in store
  } finally { submitPasswordChangeLoading.value = false }
}

function cleanupPasswordModal() {
  if (resendIntervalPass) { clearInterval(resendIntervalPass); resendIntervalPass = null; }
  passwordSendLoading.value = false;
  submitPasswordChangeLoading.value = false;
  passwordCodeSent.value = false;
  passwordVerificationCode.value = '';
  passwordNew.value = '';
  passwordConfirmNew.value = '';
  generatedPasswordCode.value = '';
  resendSecondsPass.value = 0;
}

function closeMobileModal() {
  showMobileModal.value = false;
  cleanupMobileModal();
}

// cleanup when closing
function cleanupMobileModal() {
  if (resendInterval) { clearInterval(resendInterval); resendInterval = null; }
  resendSeconds.value = 0;
  mobileSendLoading.value = false;
  mobileCodeSent.value = false;
  mobileVerificationCode.value = '';
  newMobileNumber.value = '';
  generatedMobileCode.value = '';
}

function sendMobileChangeCode() {
  if (!accountInfo.value.mobileNumber) {
    toast.error('شماره موبایل فعلی نامعتبر است');
    return;
  }
  mobileSendLoading.value = true;
  // simulate sending SMS
  setTimeout(() => {
    mobileSendLoading.value = false;
    mobileCodeSent.value = true;
    generatedMobileCode.value = String(Math.floor(100000 + Math.random() * 900000));
    // In real app you wouldn't show the code in UI; for demo show a brief info
    toast.info('کد تایید ارسال شد');
    // start resend countdown
    resendSeconds.value = 60;
    if (resendInterval) clearInterval(resendInterval);
    resendInterval = setInterval(() => {
      resendSeconds.value -= 1;
      if (resendSeconds.value <= 0) {
        clearInterval(resendInterval);
        resendInterval = null;
      }
    }, 1000);
  }, 900);
}

function submitMobileChange() {
  if (!mobileCodeSent.value) {
    toast.error('لطفاً ابتدا کد تغییر را ارسال کنید');
    return;
  }
  if (!mobileVerificationCode.value) {
    toast.error('لطفاً کد تایید را وارد کنید');
    return;
  }
  if (mobileVerificationCode.value !== generatedMobileCode.value) {
    toast.error('کد تایید اشتباه است');
    return;
  }
  if (!newMobileNumber.value) {
    toast.error('لطفاً شماره جدید را وارد کنید');
    return;
  }

  // Update local state and persist
  accountInfo.value.mobileNumber = newMobileNumber.value;
  const companyInfo = JSON.parse(localStorage.getItem('companyInfo')) || {};
  const updatedInfo = { ...companyInfo, ...accountInfo.value };
  localStorage.setItem('companyInfo', JSON.stringify(updatedInfo));
  toast.success('شماره همراه با موفقیت تغییر کرد');
  cleanupMobileModal();
  showMobileModal.value = false;
}

// simple currency filter replacement: format number with thousands separator
// formatters moved to src/utils/formatters.js
const currency = formatCurrency;
  return {
    companyTitle,
    panelNumber,
    activeServices,
    messages,
    usedStorage,
    totalStorage,
    storagePercent,
    recentActivities,
    lastSettingsUpdate,
    lastLogin,
    attachedFilesCount,
    attachmentsSeries,
    maxAttachments,
    purchaseDate,
    expiryDate,
    licenseInfoLoading,
    licenseStatus,
    licenseStatusTone,
    infoLearnUrl,
    showChangePassword,
    oldPassword,
    newPassword,
    confirmPassword,
    showMobileModal,
    mobileSendLoading,
    mobileCodeSent,
    mobileVerificationCode,
    newMobileNumber,
    generatedMobileCode,
    resendSeconds,
    showPasswordModal,
    passwordSendLoading,
    submitPasswordChangeLoading,
    passwordCodeSent,
    passwordVerificationCode,
    passwordNew,
    passwordConfirmNew,
    generatedPasswordCode,
    resendSecondsPass,
    showTwoFactorMethodModal,
    selectedTwoFactorMethod,
    twoFactorLoading,
    twoFactorError,
    googleSetupData,
    googleVerificationCode,
    twoFactorStatus,
    twoFactorMethods,
    showRenewalModal,
    renewalPaymentLoading,
    selectedRenewalGateway,
    renewalAmount,
    renewalGateways,
    monthlyBills,
    monthStart,
    selectedTariffIds,
    showTariffInvoiceModal,
    tariffPurchaseLoading,
    tariffLoading,
    tariffItems,
    selectedTariffItems,
    selectedTariffTotal,
    accountInfo,
    imageInput,
    companyFields,
    initUserAreaPage,
    displayValue,
    triggerImageUpload,
    handleImageUpload,
    confirmTariffPurchase,
    openTariffInvoiceModal,
    cancelTariffPurchase,
    fetchLicenseInfo,
    goToSettings,
    goToSubsystems,
    openBilling,
    openSupport,
    manageSessions,
    buyStorage,
    renewLicense,
    closeRenewalModal,
    payRenewal,
    goToFiles,
    openMonthlyBills,
    submitChangePassword,
    openMobileModal,
    openPasswordModal,
    closePasswordModal,
    openTwoFactorMethodModal,
    closeTwoFactorMethodModal,
    submitTwoFactorMethod,
    startGoogleAuthenticatorSetup,
    disableTwoFactor,
    sendPasswordChangeCode,
    submitPasswordChangeModal,
    closeMobileModal,
    sendMobileChangeCode,
    submitMobileChange,
    currency,
  };
});
